// lib/helpers/intercom.js

/**
 * Hides the default Intercom launcher
 */
export const hideDefaultIntercomLauncher = () => {
  if (window.Intercom) {
    window.Intercom('update', {
      hide_default_launcher: true,
      custom_launcher_selector: '[data-intercom-trigger="true"]'
    });
  }
};

export const waitForMessengerAndPosition = () => {
  const maxAttempts = 10;
  let attempts = 0;

  const interval = setInterval(() => {
    const frame = document.querySelector('.intercom-messenger-frame');
    if (frame) {
      positionIntercomMessenger();
      clearInterval(interval);
    }

    if (++attempts > maxAttempts) {
      clearInterval(interval);
    }
  }, 100);
};

/**
 * Shows the default Intercom launcher
 */
export const showDefaultIntercomLauncher = () => {
  if (window.Intercom) {
    window.Intercom('update', {
      hide_default_launcher: false,
      custom_launcher_selector: null
    });
  }
};

/**
 * Creates and injects CSS to hide Intercom elements
 * @returns {HTMLStyleElement} The created style element
 */
export const injectIntercomStyles = () => {
  const style = document.createElement('style');
  style.textContent = `
    .intercom-lightweight-app { display: none !important; visibility: hidden !important; }
    #intercom-container .intercom-launcher-frame { display: none !important; visibility: hidden !important; }
    #intercom-container div[class*="intercom-launcher"] { display: none !important; visibility: hidden !important; }
  `;
  document.head.appendChild(style);
  return style;
};

/**
 * Positions the Intercom messenger relative to a custom button
 */
export const positionIntercomMessenger = () => {
  const customButton = document.querySelector('[data-intercom-trigger="true"]');
  if (!customButton) return;

  const buttonRect = customButton.getBoundingClientRect();

  const intercomMessenger = document.querySelector('.intercom-messenger-frame');
  if (intercomMessenger) {
    intercomMessenger.style.transition = 'none';
    intercomMessenger.style.top = `${buttonRect.bottom + 15}px`;
    intercomMessenger.style.left = `10px`;
    intercomMessenger.style.right = `${buttonRect.right}px`;
    intercomMessenger.style.bottom = 'auto';
    intercomMessenger.style.position = 'fixed';
    intercomMessenger.style.animation = 'none';
    intercomMessenger.style.transform = 'none';
    intercomMessenger.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
  }

  const defaultLaunchers = document.querySelectorAll(
    '#intercom-container .intercom-launcher-frame'
  );
  defaultLaunchers.forEach((launcher) => {
    launcher.style.display = 'none';
    launcher.style.visibility = 'hidden';
  });
};

/**
 * Resets the Intercom messenger to default position
 */
export const resetMessengerPosition = () => {
  const intercomMessenger = document.querySelector('.intercom-messenger-frame');
  if (intercomMessenger) {
    intercomMessenger.style.top = '';
    intercomMessenger.style.left = '';
    intercomMessenger.style.right = '';
    intercomMessenger.style.bottom = '';
    intercomMessenger.style.position = '';
    intercomMessenger.style.animation = '';
    intercomMessenger.style.transform = '';
    intercomMessenger.style.boxShadow = '';
    intercomMessenger.style.transition = '';
  }
};

/**
 * Sets up Intercom event handlers
 * @param {Function} setIsOpen - Function to update open state
 * @param {Function} setUnreadCount - Function to update unread count
 * @param {React.MutableRefObject} ignoreResets - Ref to track reset behavior
 */
export const setupIntercomEventHandlers = (setIsOpen, setUnreadCount, ignoreResets) => {
  if (!window.Intercom) return;

  window.Intercom('onHide', () => {
    setIsOpen(false);
    ignoreResets.current = true;
  });

  window.Intercom('onShow', () => {
    setIsOpen(true);
    setUnreadCount(0);
    ignoreResets.current = false;

    positionIntercomMessenger();
  });

  window.Intercom('onUnreadCountChange', function (count) {
    if (ignoreResets?.current) {
      if (count > setUnreadCount((prevCount) => (count > prevCount ? count : prevCount))) {
        setUnreadCount(count);
      }
    } else {
      setUnreadCount(count);
    }
  });
};

/**
 * Checks for Intercom and initializes it with our custom settings
 * @param {Function} callback - Function to call when Intercom is ready
 * @returns {number} interval ID for cleanup
 */
export const initializeIntercom = (callback) => {
  const intervalId = setInterval(() => {
    if (window.Intercom) {
      clearInterval(intervalId);
      hideDefaultIntercomLauncher();

      if (callback) callback();

      return window.Intercom('getUnreadCount');
    }
  }, 100);

  return intervalId;
};
